import { createFileRoute, Link } from "@tanstack/react-router";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { Reveal } from "@/components/site/wow/Reveal";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { aeoGeo } from "@/lib/seo/schema";
import { ContactForm } from "@/components/site/ContactForm";


export const Route = createFileRoute("/en_/contact")({
  head: () =>
    ({
      meta: [
        { title: "Contact Fenny — Talk to a video producer today" },
        {
          name: "description",
          content:
            "Get in touch with Fenny — Sweden's video subscription studio. We reply within one business day. Strategy calls are free.",
        },
        { property: "og:title", content: "Contact Fenny" },
        {
          property: "og:description",
          content:
            "Talk to a senior producer. Free 30-minute strategy call, no obligations.",
        },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:url", content: "https://fenny.se/en/contact" },
      ],
      links: [
        { rel: "canonical", href: "https://fenny.se/en/contact" },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/contact", name: "Contact Fenny — Talk to a video producer today", description: "Get in touch with Fenny — Sweden's video subscription studio. We reply within one business day. Strategy calls are free."})) },
      ],
    }) as never,
  component: ContactPage,
});

function ContactPage() {
  return (
    <div>
      <section className="relative overflow-hidden pt-32 pb-12 md:pt-44 md:pb-20">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_80%_10%,color-mix(in_oklab,var(--mint)_10%,transparent),transparent_70%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="mb-6 text-xs uppercase tracking-[0.22em] text-butter">
            Get in touch
          </div>
          <KineticHeading
            text="Let's make your next video together."
            className="max-w-[20ch] text-balance text-5xl font-semibold leading-[0.98] text-foreground md:text-7xl"
            highlight={{ indices: [4], className: "text-mint italic font-serif font-normal" }}
          />
          <p className="mt-10 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl">
            Tell us what you're working on. We respond within one business day —
            often faster. The first strategy call is free and has no strings
            attached.
          </p>
        </div>
      </section>

      <Reveal>
        <section className="py-16 md:py-24">
          <div className="container-wide grid gap-12 md:grid-cols-2">
            <div className="space-y-8">
              <div className="rounded-3xl border border-white/10 bg-surface p-8">
                <Mail className="h-6 w-6 text-mint" />
                <h3 className="mt-4 text-lg font-semibold">Email</h3>
                <a
                  href="mailto:hello@fenny.se"
                  className="mt-2 block text-base text-muted-foreground hover:text-foreground"
                >
                  hello@fenny.se
                </a>
              </div>
              <div className="rounded-3xl border border-white/10 bg-surface p-8">
                <Phone className="h-6 w-6 text-mint" />
                <h3 className="mt-4 text-lg font-semibold">Phone</h3>
                <a
                  href="tel:+46848004706"
                  className="mt-2 block text-base text-muted-foreground hover:text-foreground"
                >
                  +46 8 480 04 706
                </a>
              </div>
              <div className="rounded-3xl border border-white/10 bg-surface p-8">
                <MapPin className="h-6 w-6 text-mint" />
                <h3 className="mt-4 text-lg font-semibold">Studio</h3>
                <p className="mt-2 text-base text-muted-foreground">
                  Stockholm, Sweden — meetings on Zoom or in person.
                </p>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-surface p-8 md:p-10">
              <h2 className="text-2xl font-semibold">Book a free 30-minute call</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                A senior producer walks you through formats, cadence and
                pricing — no sales script, no obligation.
              </p>
              <div className="mt-8">
                <Magnetic strength={0.3}>
                  <a
                    href="#contact"
                    className="group inline-flex items-center gap-3 rounded-full bg-mint px-8 py-4 text-base font-semibold text-mint-foreground"
                  >
                    Book a strategy call
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </Magnetic>
              </div>
              <ul className="mt-10 space-y-3 text-sm text-muted-foreground">
                <li>· Response within one business day</li>
                <li>· English, Swedish and Norwegian</li>
                <li>· EU GDPR compliant — your data stays in the EU</li>
              </ul>
            </div>
          </div>
        </section>
      </Reveal>

      <ContactForm
        id="contact"
        location="en-contact"
        eyebrow="Contact"
        title="Tell us about your project"
        lede="Fill in the form and a senior producer gets back to you within one business day. Every enquiry lands directly with our team — nothing gets lost in an inbox."
      />


      <Reveal>
        <section className="py-24 md:py-32 border-t border-white/10">
          <div className="container-wide text-center">
            <h2 className="mx-auto max-w-3xl text-balance text-4xl font-semibold leading-tight md:text-6xl">
              Prefer to read first?
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/en/pricing"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-medium text-foreground/90 hover:bg-white/5"
              >
                See pricing
              </Link>
              <Link
                to="/en/services"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-medium text-foreground/90 hover:bg-white/5"
              >
                Explore services
              </Link>
            </div>
          </div>
        </section>
      </Reveal>
    </div>
  );
}
